// src/components/MateriasLista.js
import React, { useMemo, useState } from 'react';
import { STATUS } from '../utils/careerLogic';
import { useOptionalStore } from '../store/store';
import { useCPOContext } from '../contexts/CPOContext';
import { isFromCPCOnwards, sortByEstadoThenNombre, splitFechaToMonthYear, buildFechaFromMonthYear } from '../utils/filters';

// Renderiza una lista de materias en modo simple o tabla detallada.
// - onNotaChange: callback para actualizar notas/fechas.
// - mode: 'list' | 'table'
const MateriasLista = ({ materias, onNotaChange = () => {}, onMateriaChangeStatus, mode = 'table' }) => {

  const store = useOptionalStore();
  const { state: cpoState, upsertMateriaCPO, puntos, cpcCursadas } = useCPOContext();

  const materiasFromProps = materias || {};
  const materiasEffective = materiasFromProps;

  const [filtroEstado, setFiltroEstado] = useState('TODOS');
  const [search, setSearch] = useState('');
  const [orden, setOrden] = useState('nombre');
  

  // 1) Base: materias del mapa (CPC/CPO reales)
  const materiasArrayBase = useMemo(() => Object.values(materiasEffective || {}), [materiasEffective]);

  // 2) Materias aprobadas/cargadas vía CPO (modal/contexto)
  const materiasCPOFromContext = useMemo(() => {
    return Object.values(cpoState?.materias || {})
      .filter((m) => (m.estado || STATUS.NO_CURSADA) === STATUS.CURSADA_APROBADA)
      .map((m) => ({
      id: m.id,
      nombre: m.nombre || m.id,
      categoria: m.categoria || 'CPO Libre Elección',
      estado: m.estado || STATUS.NO_CURSADA,
      notaFinal: typeof m.notaFinal === 'number' ? m.notaFinal : -2,
      fecha: null,
      creditos: m.creditos || 0,
      // Flag para distinguir en UI si no existe en plan original
      __fromCPOContext: true,
    }));
  }, [cpoState]);

  // 3) Fusionar: mantener materias del mapa (sin CBC) + sumar CPO aprobadas del contexto que no existan ya
  const materiasFusionadas = useMemo(() => {
    const base = materiasArrayBase.filter(isFromCPCOnwards);
    const baseIds = new Set(base.map((m) => m.id));
    const extra = materiasCPOFromContext.filter((x) => !baseIds.has(x.id));
    return [...base, ...extra];
  }, [materiasArrayBase, materiasCPOFromContext]);

  const promedio = useMemo(() => {
    const aprobadas = materiasFusionadas.filter(
      m => m.estado === STATUS.CURSADA_APROBADA && m.notaFinal >= 0
    );
    if (aprobadas.length === 0) return 0;
    const suma = aprobadas.reduce((acc, m) => acc + Number(m.notaFinal), 0);
    return (suma / aprobadas.length).toFixed(2);
  }, [materiasFusionadas]);

  const isLectoOrPractico = (m) => m && (m.id === 'LECTO' || m.id === 'PRACTICO_PROFESIONAL');
  const isAprobada = (m) => m && m.estado === STATUS.CURSADA_APROBADA;

  const estadoLabel = (estado) => {
    switch (estado) {
      case STATUS.CURSADA_APROBADA: return 'Aprobada';
      case STATUS.CURSANDO: return 'Cursando';
      case STATUS.PUEDE_CURSAR: return 'Puede cursar';
      default: return 'No cursada';
    }
  };

  const materiasFiltradas = useMemo(() => {
    const byEstado = (m) => {
      if (filtroEstado === 'TODOS') return true;
      return m.estado === filtroEstado;
    };
    const bySearch = (m) => !search || String(m.nombre || '').toLowerCase().includes(search.toLowerCase());
    return materiasFusionadas.filter((m) => byEstado(m) && bySearch(m));
  }, [materiasFusionadas, filtroEstado, search]);

  const materiasOrdenadas = useMemo(() => {
    return [...materiasFiltradas].sort((a, b) => {
      if (orden === 'estado') return sortByEstadoThenNombre(a, b);
      if (orden === 'nombre') return String(a.nombre || '').localeCompare(String(b.nombre || ''));
      if (orden === 'nota') return (b.notaFinal ?? -1) - (a.notaFinal ?? -1);
      if (orden === 'fecha') {
        const da = a.fecha ? new Date(a.fecha) : new Date(0);
        const db = b.fecha ? new Date(b.fecha) : new Date(0);
        return db - da;
      }
      return 0;
    });
  }, [materiasFiltradas, orden]);

  const exportarJSON = () => {
    const data = materiasOrdenadas.map(({ id, nombre, estado, notaFinal, fecha, categoria, updatedAt }) => ({
      id,
      nombre,
      estado,
      notaFinal,
      fecha,
      categoria,
      updatedAt: updatedAt || '',
    }));
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'materias_notas.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportarCSVvisible = () => {
    const rows = [['materia','codigo','area','estado','nota','mes','anio','observaciones','updatedAt']];
    for (const m of materiasOrdenadas) {
      const { month, year } = splitFechaToMonthYear(m.fecha);
      const isLectoOrPractico = (m.id === 'LECTO' || m.id === 'PRACTICO_PROFESIONAL');
      const isAprobada = m.estado === STATUS.CURSADA_APROBADA;
      const nota = isLectoOrPractico && isAprobada ? '' : (m.notaFinal >= 0 ? String(m.notaFinal) : '');
      const mes = isLectoOrPractico && isAprobada ? '' : (month || '');
      const anio = isLectoOrPractico && isAprobada ? '' : (year || '');
      const obs = isLectoOrPractico && isAprobada ? 'APROBADO' : '';
      rows.push([
        String(m.nombre || ''),
        String(m.id || ''),
        String(m.categoria || ''),
        String(m.estado || ''),
        nota,
        mes,
        anio,
        obs,
        String(m.updatedAt || ''),
      ]);
    }
    const csv = rows.map((r) => r.map((x) => '"' + String(x).replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'materias_visible.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // export CSV exacto visible: usar exportarCSVvisible()

  const handleNotaChangeValidated = (id, value, materia) => {
    // Validar 4-10 o vacío
    if (value === '') {
      // CPO extras: persistir en contexto
      if (materia?.__fromCPOContext || String(id).startsWith('cpo_')) {
        upsertMateriaCPO({ id, nombre: materia?.nombre || id, creditos: materia?.creditos || 0, categoria: materia?.categoria || 'CPO Libre Elección', estado: STATUS.CURSADA_APROBADA, notaFinal: null });
      }
      onNotaChange(id, -1, materia?.fecha || null);
      return;
    }
    const notaNum = parseInt(value, 10);
    if (isNaN(notaNum) || notaNum < 0 || notaNum > 10) return; // bloquear fuera de rango (0-10)
    // CPO extras: persistir en contexto
    if (materia?.__fromCPOContext || String(id).startsWith('cpo_')) {
      upsertMateriaCPO({ id, nombre: materia?.nombre || id, creditos: materia?.creditos || 0, categoria: materia?.categoria || 'CPO Libre Elección', estado: STATUS.CURSADA_APROBADA, notaFinal: notaNum });
    }
    onNotaChange(id, notaNum, materia?.fecha || null);
  };

  const handleFechaChange = (id, month, year, materia) => {
    const fecha = buildFechaFromMonthYear(month, year) || null;
    if (store?.setSubjectGrade) {
      store.setSubjectGrade(id, { nota: typeof materia?.notaFinal === 'number' ? materia.notaFinal : null, mes: month ? Number(month) : null, anio: year ? Number(year) : null });
    } else {
      onNotaChange(id, materia?.notaFinal ?? -1, fecha);
    }
  };

  const getEstadoColor = (estado) => {
    switch (estado) {
      case STATUS.NO_CURSADA:
        return 'var(--error-color)';
      case STATUS.PUEDE_CURSAR:
        return 'var(--warning-color)';
      case STATUS.CURSANDO:
        return 'var(--info-color)';
      case STATUS.CURSADA_APROBADA:
        return 'var(--success-color)';
      default:
        return 'var(--text-secondary)';
    }
  };

  if (mode === 'list') {
    return (
      <div style={{ padding: '20px', color: 'var(--text-primary)' }}>
        <h2>Lista de Materias</h2>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {materiasOrdenadas.map(m => (
            <li key={m.id} style={{ margin: '4px 0' }}>
              {m.nombre}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', background: 'var(--bg-primary)', color: 'var(--text-primary)', minHeight: '100vh' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '10px'
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <h2 style={{ margin: 0 }}>📋 Materias (CPC y CPO)</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: '600' }}>CPC cursadas: <span style={{ color: 'var(--accent-color)' }}>{cpcCursadas || 0}</span>/14</span>
            <span style={{ fontWeight: '600' }}>Puntos CPO: <span style={{ color: 'var(--accent-color)' }}>{puntos?.total || 0}</span>/69</span>
            <span>Promedio finales: <strong style={{ color: 'var(--accent-color)' }}>{promedio}</strong></span>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={exportarJSON}
            style={{
              padding: '8px 16px',
              background: 'var(--accent-color)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            📊 Exportar JSON
          </button>
          <button
            onClick={exportarCSVvisible}
            style={{
              padding: '8px 16px',
              background: 'var(--accent-color)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            📄 Exportar CSV
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Buscar materia..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            padding: '8px 12px',
            borderRadius: '4px',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)'
          }}
        />
        <select
          value={filtroEstado}
          onChange={e => setFiltroEstado(e.target.value)}
          style={{
            padding: '8px',
            borderRadius: '4px',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
            minWidth: '120px'
          }}
        >
          <option value="TODOS">Todos los estados</option>
          {Object.values(STATUS).map(est => (
            <option key={est} value={est}>{est.replace('_', ' ')}</option>
          ))}
        </select>
        <select
          value={orden}
          onChange={e => setOrden(e.target.value)}
          style={{
            padding: '8px',
            borderRadius: '4px',
            background: 'var(--bg-secondary)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border-color)',
            minWidth: '120px'
          }}
        >
          <option value="estado">Ordenar por estado</option>
          <option value="nombre">Ordenar por nombre</option>
          <option value="fecha">Ordenar por fecha</option>
          <option value="nota">Ordenar por nota</option>
        </select>
      </div>

      <div style={{ overflowX: 'auto', background: 'var(--bg-secondary)', borderRadius: '8px', boxShadow: 'var(--shadow-medium)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'var(--bg-primary)' }}>
              <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--border-color)' }}>Materia</th>
              <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--border-color)' }}>Estado</th>
              <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--border-color)' }}>Mes/Año</th>
              <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--border-color)' }}>Nota final</th>
              <th style={{ textAlign: 'left', padding: '12px 8px', borderBottom: '2px solid var(--border-color)' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {materiasOrdenadas.map(m => {
              const { month, year } = splitFechaToMonthYear(m.fecha);
              return (
              <tr key={m.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '12px 8px', fontWeight: '500' }}>{m.nombre}</td>
                <td style={{ padding: '12px 8px' }}>
                  {(() => {
                    const color = getEstadoColor(m.estado);
                    const baseStyle = {
                      color,
                      fontWeight: 'bold',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: `${color}20`,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    };
                    const label = m.estado.replace('_', ' ');
                    const icon = m.estado === STATUS.CURSADA_APROBADA
                      ? '✅'
                      : m.estado === STATUS.CURSANDO
                        ? '⏳'
                        : m.estado === STATUS.PUEDE_CURSAR
                          ? '📅'
                          : '🔒';
                    return (
                      <span style={baseStyle}>
                        <span>{icon}</span>
                        <span>{label}</span>
                      </span>
                    );
                  })()}
                </td>
                 <td style={{ padding: '12px 8px' }}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select
                      value={month}
                      onChange={(e) => handleFechaChange(m.id, e.target.value, year, m)}
                      style={{
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        padding: '6px'
                      }}
                      disabled={isLectoOrPractico(m) && isAprobada(m)}
                    >
                      <option value="">Mes</option>
                      {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map((mm) => (
                        <option key={mm} value={mm}>{mm}</option>
                      ))}
                    </select>
                    <select
                      value={year}
                      onChange={(e) => handleFechaChange(m.id, month, e.target.value, m)}
                      style={{
                        background: 'var(--bg-primary)',
                        color: 'var(--text-primary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        padding: '6px'
                      }}
                      disabled={isLectoOrPractico(m) && isAprobada(m)}
                    >
                      <option value="">Año</option>
                      {Array.from({ length: 12 }, (_, i) => String(new Date().getFullYear() - i)).map((yy) => (
                        <option key={yy} value={yy}>{yy}</option>
                      ))}
                    </select>
                  </div>
                </td>
                <td style={{ padding: '12px 8px' }}>
                  <input
                    type="number"
                    min="0"
                    max="10"
                    value={m.notaFinal >= 0 ? m.notaFinal : ''}
                     disabled={m.estado !== STATUS.CURSADA_APROBADA || (isLectoOrPractico(m) && isAprobada(m))}
                    onChange={(e) => handleNotaChangeValidated(m.id, e.target.value, m)}
                    onBlur={(e) => handleNotaChangeValidated(m.id, e.target.value, m)}
                    style={{
                      background: m.estado === STATUS.CURSADA_APROBADA ? 'var(--bg-primary)' : 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      padding: '6px',
                      width: '60px',
                      fontSize: '14px',
                      opacity: m.estado === STATUS.CURSADA_APROBADA ? 1 : 0.5
                    }}
                  />
                </td>
                <td style={{ padding: '12px 8px' }}>
                  {onMateriaChangeStatus && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => onMateriaChangeStatus(m.id, STATUS.NO_CURSADA)}
                        style={{ padding: '6px 8px' }}
                        title="Marcar No cursada"
                      >✖</button>
                      <button
                        onClick={() => onMateriaChangeStatus(m.id, STATUS.PUEDE_CURSAR)}
                        style={{ padding: '6px 8px' }}
                        title="Marcar Puede cursar"
                      >🕒</button>
                      <button
                        onClick={() => onMateriaChangeStatus(m.id, STATUS.CURSANDO)}
                        style={{ padding: '6px 8px' }}
                        title="Marcar Cursando"
                      >📘</button>
                      <button
                         onClick={() => {
                           if (isLectoOrPractico(m)) {
                             onMateriaChangeStatus(m.id, STATUS.CURSADA_APROBADA);
                           } else {
                             const { month, year } = splitFechaToMonthYear(m.fecha);
                             const notaNum = Number(m.notaFinal);
                             if (!(notaNum >= 4 && notaNum <= 10) || !month || !year) {
                               alert('Para aprobar: nota 4..10 y mes/año obligatorios (excepto Lectocomprensión y Práctico Profesional)');
                               return;
                             }
                             onMateriaChangeStatus(m.id, STATUS.CURSADA_APROBADA);
                           }
                         }}
                        style={{ padding: '6px 8px' }}
                        title="Marcar Cursada Aprobada"
                      >✓</button>
                      
                    </div>
                  )}
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MateriasLista;
